// One grouped trip: the individual TaskPogoTrip legs that share a Trip Name
// (or a single unnamed leg), listed exactly like the pre-grouping trips table.
// Rows open a leg's detail page. Add leg -- the row at the foot of the table --
// creates a stub pre-named to stay in this group and navigates to that leg's
// detail page for editing. Rename renames the trip, which is a write to every
// leg (see handleRename). Export JSON downloads the whole trip; importing one
// lives on the trips list page (a file import creates a NEW trip, so it isn't
// an action on this one).
//
// The group is identified by a TripGroups key in the URL ('n:<name>' / 't:<id>',
// URL-encoded). The trips list and item card hand the group over in router
// state for an instant first paint; we still re-fetch the full leg set by key
// so a group that was only partially loaded upstream (paginated list, or the
// item card's bounded window) shows every leg here.

import { useState, useEffect, useMemo, useRef, useCallback, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useParams, useLocation } from 'react-router-dom';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { Confirm, DataCard, Spinner, useStatusMessage } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import { urlForTrips, urlForTrip, urlForTripGroup } from '../../fleet-shared/Urls.mjs';
import { weekdayShortDate } from '../../fleet-shared/Common.mjs';
import {
  tripFields,
  tripListColumns,
  sortTripsAscending,
} from '../../fleet-shared/TripFields.mjs';
import { useCopyLegToLogbook } from './useCopyLegToLogbook';
import {
  groupTrips,
  findTripGroup,
  isNamedGroupKey,
  isTripGroupIdKey,
  groupNameFromKey,
  tripGroupIdFromKey,
  tripIdentityDefaults,
  tripGroupKey,
  nextLegDefaults,
  reorderLegsByDate,
  tripGroupDateRange,
  tripGroupSummary,
} from '../../fleet-shared/TripGroups.mjs';
import {
  adjacentTrips,
  mergedTripIdentity,
  orderedMergedLegs,
  recordsToRefile,
  seamDaysUpdates,
} from '../../fleet-shared/TripMerge.mjs';
import {
  legLane,
  loadAllTripsFresh,
  loadGroupLegs,
  persistLegDates,
  pendingLegLanes,
  legCascadesPending,
  settledLegCascades,
} from '../../utilities/TripSchedule.mjs';
import {
  routeFromTrip,
  hasPlacedWaypoints,
  tripWithResolvedEndpoints,
} from '../../fleet-shared/TripRoute.mjs';
import { loadPlaceIndex } from '../../utilities/places.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { useIsMdUp } from '../../utilities/useIsMdUp';
import { placeCell } from '../../fleet-shared/PlaceFields.mjs';
import { serializeTrip, suggestedTripFileName } from '../../fleet-shared/TripIO.mjs';
import { downloadJson } from '../../utilities/downloadFile.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { CardHeader } from '../../components/CardHeader';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import CombineTripsModal from './CombineTripsModal';
import RenameTripModal from './RenameTripModal';
import ShareTripModal from './ShareTripModal';
import WeatherEmailModal from './WeatherEmailModal';
import {
  shareTripAvailable,
  removeTripShareForGroup,
  repointTripShares,
  tripShareRowsForMerge,
} from '../../utilities/shareTrip.mjs';
import {
  tripWeatherEmailAvailable,
  removeTripWeatherSubscriptionsForGroup,
  repointTripWeatherSubscriptions,
  tripWeatherRowsForMerge,
} from '../../utilities/tripWeatherEmail.mjs';

// Lazy: TripGroupMap is a maplibre-gl import path (~250 KB gz); keep it out
// of the entry bundle, same as Trip.jsx does with TripRouteMap.
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

const wideColumns = tripListColumns
  .map((key) => tripFields.find((f) => f.key === key))
  .filter(Boolean);

// The one column this table invents. From and To are two registry fields, but
// below md they share a cell -- so the merged column needs a field of its own,
// and it is deliberately NOT in TripFields.mjs: `tripListColumns` is shared
// with the public shared-trip page, and 'route' is a rendering of two columns
// rather than a value any leg carries.
const routeField = { name: 'Route', key: 'route', type: 'route' };

// The legs table below Bootstrap's md breakpoint.
//
// Days drops out: it dates the NEXT leg (TripGroups.cascadeLegDates), which is
// an editing fact, and the Date column beside it already answers the question a
// reader is asking. From and To merge into one stacked Route cell, because two
// full columns of harbour names is what pushed this table wider than a phone,
// and a leg's endpoints are read together anyway.
//
// Distance is renamed rather than dropped. Measured on a 375px screen, its
// HEADING was setting the column: 72px for a word, over cells holding "65".
// "nm" is 30px, and it is the more useful label of the two -- the number was
// never dimensionless, and every other surface in the app already spells the
// unit that way ("125nm from Roche Harbor to Nanaimo").
//
// What's left -- Date, Route, nm, the reorder arrows and the chevron -- fits
// 375px with no sideways scroll, which is the whole point: the scroll
// container used to carry the Date column off the left edge, and a leg you
// cannot date is a row you cannot place.
const narrowColumns = ['date', 'route', 'distance']
  .map((key) => {
    if (key === 'route') return routeField;
    const field = tripFields.find((f) => f.key === key);
    return field && key === 'distance' ? { ...field, name: 'nm' } : field;
  })
  .filter(Boolean);

// Render one cell value for a given leg column field. Dates carry their weekday
// here (weekdayShortDate) rather than reading as elsewhere in the app: these
// rows ARE the trip's schedule, and "which day of the week is that leg" is the
// question the column exists to answer.
//
// Returns { text, title }: a From/To column names a place the gazetteer may
// have a shorter name for, and `title` carries the full one on those cells only.
// `text` is a node for the merged Route column and a string everywhere else.
function cellValue(trip, field, placeIndex) {
  if (field.type === 'route') {
    return routeCellValue(trip, placeIndex);
  }
  const value = trip[field.key];
  if (field.type === 'date') {
    return { text: weekdayShortDate(value ?? null) };
  }
  if (value === null || value === undefined) {
    return { text: '' };
  }
  if (field.type === 'place') {
    return placeCell(value, placeIndex);
  }
  return { text: value };
}

// The narrow layout's merged From/To cell: the two endpoints stacked, with an
// arrow marking which way round they go.
//
// It goes through placeCell for each half exactly as the wide columns do, so
// the gazetteer's short names are used here too -- the reason this column can
// afford to hold both. The title carries the pair in FULL, unconditionally
// (unlike placeCell, which titles only what it shortened): in one narrow
// column even an unshortened name is likely to wrap or run out of room, which
// is the case a hover has to rescue.
//
// A leg with only one endpoint filled in shows that one on its own; a leg with
// neither renders an empty cell rather than a stray arrow.
function routeCellValue(trip, placeIndex) {
  const named = (value) => value != null && String(value).trim() !== '';
  const from = named(trip.from) ? placeCell(trip.from, placeIndex) : null;
  const to = named(trip.to) ? placeCell(trip.to, placeIndex) : null;
  if (!from && !to) {
    return { text: '' };
  }
  const title = [trip.from, trip.to].filter(named).map((v) => String(v).trim()).join(' → ');
  return {
    title,
    text: (
      <>
        {from && <span className="d-block">{from.text}</span>}
        {to && <span className="d-block">→ {to.text}</span>}
      </>
    ),
  };
}

// "3 legs" / "1 leg", for the Combine dialog's description of a trip. A trip
// whose legs aren't loaded yet says nothing rather than "0 legs".
function legCountLabel(count) {
  if (!count) return '';
  return `${count} ${count === 1 ? 'leg' : 'legs'}`;
}

// A leg's note, or '' when it hasn't got one. The note is rendered in full on a
// continuation row rather than announced by an icon in a column of its own: a
// Notes column costs the same width on every row whether or not anything is in
// it, and told the reader only that there was something they couldn't see.
function legNote(trip) {
  return typeof trip?.notes === 'string' ? trip.notes.trim() : '';
}

// Add back legs the caller just wrote that the fetch didn't return yet.
//
// Legs are read via tripsByItemId, a GSI, and an index can lag the write that
// fed it -- so a page that writes legs and navigates straight here can arrive
// before that read agrees. Two callers do exactly that: the trips list's JSON
// import (which CREATES the legs, so the fetch can come back short or empty)
// and this page's own Rename (which rewrites every leg's name, so the fetch can
// still be grouping them under the OLD key and return nothing at all under the
// new one). `known` are mutation responses -- base-table rows -- so they
// demonstrably exist whatever the index currently says.
//
// The fetch still wins for everything it does return; this only re-adds ids
// missing from it. Callers must therefore pass `known` ONLY for legs they wrote
// themselves moments ago (TripGroup gates this on `legsJustWritten`): for an
// ordinary handover the snapshot may be stale, and a missing leg there means
// someone deleted it -- re-adding would resurrect it on screen.
function withKnownLegs(fetched, known) {
  const seen = new Set((fetched ?? []).map((leg) => leg.id));
  const missing = (known ?? []).filter((leg) => leg?.id && !seen.has(leg.id));
  // sortedLegs re-sorts date-ascending, so appending is enough.
  return missing.length > 0 ? [...(fetched ?? []), ...missing] : fetched;
}

// This group's legs out of the item's, by the key the page is addressed by.
//
// findTripGroup rather than an === match, for the reason it always is here: a
// bookmark minted before trips had ids holds 'n:<name>', and the trip it named
// carries an id now. [] rather than null when the key resolves to nothing --
// the page's "This trip has no legs" empty state is keyed on that difference.
function legsOfGroup(allLegs, groupKey) {
  return findTripGroup(groupTrips(allLegs ?? []), groupKey)?.legs ?? [];
}

// The item's legs with rows the caller just WROTE taking precedence.
//
// withKnownLegs' counterpart for the whole-item list, and it replaces where
// that one appends. The difference is what each list is narrowed by: `legs`
// only ever holds rows the fetch grouped under this key, so a row the GSI is
// still serving under the OLD name is simply absent there. In the item's list
// it is PRESENT, carrying its pre-write name and id -- which would draw a
// phantom trip beside this one, with a Combine button offering to merge the
// trip that was just merged.
function withWrittenLegs(fetched, written) {
  const byId = new Map((written ?? []).filter((leg) => leg?.id).map((leg) => [leg.id, leg]));
  if (byId.size === 0) return fetched;
  const seen = new Set();
  const merged = (fetched ?? []).map((leg) => {
    const replacement = byId.get(leg?.id);
    if (replacement) seen.add(leg.id);
    return replacement ?? leg;
  });
  const missing = [...byId.values()].filter((leg) => !seen.has(leg.id));
  return missing.length > 0 ? [...merged, ...missing] : merged;
}

// Reordering is a date edit (see reorderLegsByDate), which moves the trip's
// date range, whether it counts as upcoming, and where it sits in the trips
// list. The Order column used to say so in a line of text under its header;
// that line was wider than the two arrows beneath it and set the column's
// width for every row of every trip, so it's gone.
//
// Deleting a leg is the leg's own page's job (Trip.jsx). It used to be offered
// here too, on a trash button in every row, but the two implementations were
// the same delete followed by the same cascadeLegDates -- and this table's
// width is worth more to a phone than a second copy of a destructive action
// the reader can reach in one tap. The header's Delete is a different action
// with a different subject: it deletes the TRIP, which is every one of these
// rows and needs no cascade because nothing is left to re-date (confirmDelete).

// One leg row (as one or two <tr>s inside the parent <Table>). The reorder
// arrows mirror the waypoint editor's (TripWaypoints.jsx) -- same icons, same
// "Move X up" labelling -- but here a leg is identified by its 1-based position,
// which is the only thing that distinguishes two legs of the same trip at a
// glance. Every button stops propagation: the whole row is a link to the leg's
// page.
//
// A leg with a note gets a second <tr> under its own carrying the note in full.
// It's a separate row because the note has to span every column; `leg-row` /
// `leg-note-row` (App.css) are what make the pair read and hover as one row --
// the leg row's bottom rule is suppressed and either half lights both.
const LegRow = ({ trip, position, placeIndex, onDetails, onCopyToLog, onMove, canMoveUp, canMoveDown, disabled, copyDisabled, columns, showOrder, columnCount, isMdUp }) => {
  const note = legNote(trip);
  return (
  <>
  <tr
    onClick={() => onDetails(trip)}
    role="button"
    style={{ cursor: 'pointer' }}
    className={note ? 'leg-row' : undefined}
  >
    {columns.map((field) => {
      const { text, title } = cellValue(trip, field, placeIndex);
      return (
        <td
          key={field.key}
          // The date is the row's identity, so it never breaks across two
          // lines however narrow the column gets.
          className={`align-middle entered-data${field.type === 'date' ? ' text-nowrap' : ''}`}
          title={title}
        >
          {text}
        </td>
      );
    })}
    {showOrder && (
    // Side by side at md+, stacked below it: two 34px buttons in a row is a
    // wider column than any of the data beside it, and the pair reads as an
    // up/down control either way round.
    <td className="align-middle text-center">
      <div className={`d-inline-flex gap-1 ${isMdUp ? 'flex-row' : 'flex-column'}`}>
        <Button
          variant="outline-secondary"
          size="sm"
          aria-label={`Move leg ${position} up`}
          disabled={disabled || !canMoveUp}
          onClick={(e) => { e.stopPropagation(); onMove(-1); }}
        >
          <i className="fa-solid fa-chevron-up" aria-hidden="true"></i>
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          aria-label={`Move leg ${position} down`}
          disabled={disabled || !canMoveDown}
          onClick={(e) => { e.stopPropagation(); onMove(1); }}
        >
          <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
        </Button>
      </div>
    </td>
    )}
    {/* Log this day. stopPropagation is mandatory -- the whole row is a button
        that opens the leg, so without it the copy starts and the page navigates
        out from under it.

        `trip` here is a DISPLAY leg (gazetteer-resolved From/To). Only its id
        is used: useCopyLegToLogbook re-reads the stored row, the same rule
        onDetails follows, which is what keeps a resolved name out of the
        logbook. */}
    {/* The CELL stops the click too, not just the button. A disabled button
        fires no onClick of its own, so the click carried on up to the row --
        and the row opens the leg. Double-clicking Log therefore copied once and
        then navigated away, which reads exactly like the copy having failed. */}
    <td
      className="align-middle text-center"
      style={{ width: 56 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        variant="outline-secondary"
        size="sm"
        aria-label={`Copy leg ${position} to Captain's Log`}
        title="Copy to Captain's Log"
        disabled={copyDisabled}
        onClick={(e) => { e.stopPropagation(); onCopyToLog(trip); }}
      >
        <i className="fa-solid fa-book" aria-hidden="true"></i>
      </Button>
    </td>
    {/* An arrow rather than the word "More", and no trash beside it. The whole
        row already opens the leg, so this button is an affordance rather than a
        label; and deleting a leg is the leg's own page's job (Trip.jsx), which
        does the same delete plus the same date cascade -- one tap further away,
        which is the right distance for the destructive one. Between them that
        is ~95px back on every row.

        Labelled by position like the arrows: it's the only thing that tells
        two legs of the same trip apart at a glance. */}
    <td className="align-middle text-end" style={{ width: 56 }}>
      <Button
        variant="outline-primary"
        size="sm"
        aria-label={`Leg ${position} details`}
        onClick={(e) => { e.stopPropagation(); onDetails(trip); }}
      >
        <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
      </Button>
    </td>
  </tr>
  {note && (
    <tr
      onClick={() => onDetails(trip)}
      role="button"
      style={{ cursor: 'pointer' }}
      className="leg-note-row"
    >
      {/* Same small secondary type the reorder caveat used, so the note reads
          as annotation on the row above rather than another row of data.
          pre-wrap: a note the user typed as several lines stays that way. */}
      <td
        colSpan={columnCount}
        className="small text-body-secondary pt-0"
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {note}
      </td>
    </tr>
  )}
  </>
  );
};


const TripGroup = ({ user }) => {
  const { itemId, groupKey } = useParams();
  const navigate = useRootCrumbNavigate();
  const location = useLocation();

  const decodedKey = useMemo(() => {
    try {
      return decodeURIComponent(groupKey);
    } catch {
      return groupKey;
    }
  }, [groupKey]);

  const [StatusMessage, { showText, showLoading, showError, hideStatus }] = useStatusMessage();

  const [item, setItem] = useState(null);
  // Legs are seeded from router state for an instant paint, then replaced by
  // the authoritative full set fetched by key.
  const [legs, setLegs] = useState(null);

  // EVERY leg the item has, from the same read that produced `legs` -- the
  // group read already pages the whole item and groups client-side
  // (Database.listTripsForGroupKey), so this costs nothing and is what lets the
  // page know which trips are next to this one (the Combine buttons) and what
  // the item's other trip names are (Rename's collision warning).
  //
  // Deliberately a SEPARATE state rather than the source `legs` is derived
  // from: `legs` is also seeded from a router handover, which carries this
  // group's legs and not the item's, and every write path on this page updates
  // `legs` in place. Keeping them apart leaves all of that exactly as it was.
  // Null until the fetch lands, so a handover paints the table with no Combine
  // buttons until a moment later.
  const [allLegs, setAllLegs] = useState(null);

  const [exporting, setExporting] = useState(false);

  const [showRename, setShowRename] = useState(false);

  // Which neighbour a Combine was started against: { direction, group }.
  const [combining, setCombining] = useState(null);
  // What the merge will end for good -- a share link, a weather schedule. Read
  // when the dialog opens so it can say so BEFORE the write (see
  // CombineTripsModal); null while unknown, and a failed read just costs the
  // warning.
  const [combineLosses, setCombineLosses] = useState(null);

  // The public share-link dialog (ShareTripModal owns its own lookup state).
  const [showShare, setShowShare] = useState(false);

  // The daily weather email dialog (WeatherEmailModal owns its own lookup
  // state too, and re-queries on every open for the same reasons).
  const [showWeatherEmail, setShowWeatherEmail] = useState(false);

  // The delete-the-whole-trip confirmation.
  const [showDelete, setShowDelete] = useState(false);

  // A notice from a best-effort share/weather update that has to survive a
  // navigation: a rename or a key-changing merge keeps this component mounted
  // (same route), so router-state notices never re-fire, and anything shown
  // before the reload is wiped by its closing hideStatus(). A merge that does
  // NOT change the key never reloads, so it shows its notice directly instead.
  const pendingShareNotice = useRef(null);

  // A reorder is in flight. It rewrites leg dates, so the flag disables the
  // arrows and Add leg together -- two of these interleaving would race each
  // other's writes.
  const [busy, setBusy] = useState(false);

  // Copy one day into the Captain's Log. Rows hand it a display leg; the hook
  // takes only the id and re-reads the stored row.
  const [copyToLogModals, { copyLegToLogbook, copying }] = useCopyLegToLogbook({
    item,
    itemId,
    showText,
    showError,
    hideStatus,
  });

  // What the table renders. `legs` is whatever the last add/edit/fetch left in
  // state, and those paths append or map in place -- sorting here keeps the
  // on-screen order date-ascending (the order buildGroup and the reorder
  // helper both assume) without a reload.
  const sortedLegs = useMemo(() => (legs ? sortTripsAscending(legs) : null), [legs]);

  // Only a named group is a trip you can add another leg to (see the button).
  const canAddLeg = isNamedGroupKey(decodedKey);

  // A lone leg has nowhere to move, so the whole Order column would be two
  // permanently-disabled arrows -- and a single unnamed leg is the commonest
  // group there is. Drop the column rather than show dead controls.
  const showOrder = (sortedLegs?.length ?? 0) > 1;

  // Which column set the table is rendering. Branching in JS rather than
  // toggling Bootstrap's d-none/d-md-table-cell classes, for the reason
  // TaskTable and the trips calendar branch too: the narrow layout is a
  // different set of cells, not a subset of the same ones, so a CSS toggle
  // would leave both in the DOM and put the colSpan below out by however many
  // are hidden. It's also the only version jsdom can test -- media queries
  // there are inert.
  const isMdUp = useIsMdUp();
  const columns = isMdUp ? wideColumns : narrowColumns;

  // What a full-width row has to span: the leg columns plus Order (when shown),
  // Log and Details. Both the note continuation rows and the Add leg row use it.
  const columnCount = columns.length + (showOrder ? 3 : 2);

  // The shared gazetteer. Three readers: the map's route overlay (displayLegs
  // below), the legs table's From/To columns, and the group heading.
  const placeIndex = usePlaceIndex();

  // A leg as this page should DISPLAY it: route resolved against the gazetteer
  // -- so a place the user moved in /me/places moves here too -- and the
  // From/To columns re-derived from that route, so a place the user RENAMED
  // reaches the heading and the table, not just the map. The columns are a
  // save-time snapshot and resolvePlaceName can only match a row by the name
  // in them, so the waypoint's placeId is the only way back to a renamed row.
  //
  // Strictly read-only, and deliberately separate from `legs` / `sortedLegs`:
  // every write path on this page (add leg, reorder, delete, re-date) keeps
  // using the unresolved legs, so a resolve can never be persisted from here.
  // The leg's own page owns the write-back -- see Trip.jsx's routeGen.
  const resolveLeg = useCallback(
    (leg) => tripWithResolvedEndpoints(leg, placeIndex),
    [placeIndex]
  );

  const displayLegs = useMemo(
    () => (sortedLegs ? sortedLegs.map(resolveLeg) : sortedLegs),
    [sortedLegs, resolveLeg]
  );

  // Gates the map card AND the lazy chunk behind it: when no leg has a placed
  // waypoint there is nothing to draw, and rendering the Suspense anyway would
  // download maplibre-gl for a card that shows nothing.
  const anyLegOnMap = useMemo(
    () => (displayLegs ?? []).some((leg) => hasPlacedWaypoints(routeFromTrip(leg))),
    [displayLegs]
  );

  // The group re-derived from the current legs, so edits/deletes flow through.
  // Off the DISPLAY legs (`legs`, not `sortedLegs`, so groupName still takes
  // the first-seen spelling from the fetch order): the heading and summary read
  // group.from / group.to, which are flattened from the legs' columns.
  const group = useMemo(() => {
    if (!legs || legs.length === 0) return null;
    const groups = groupTrips(legs.map(resolveLeg), { placeIndex });
    // findTripGroup rather than an === match: a bookmark minted before trips
    // had ids holds 'n:<name>', and the trip it named carries an id now.
    return findTripGroup(groups, decodedKey) ?? groups[0];
  }, [legs, decodedKey, placeIndex, resolveLeg]);

  // Every trip the item has, as rows the merge rules understand. Built off
  // DISPLAY legs so a lone unnamed leg's from-to title uses the gazetteer's
  // current spelling -- that title is the only label such a trip has, and it is
  // the one branch of buildGroup that is place-shaped. Read-only: every write
  // below goes back to the stored legs.
  const tripRows = useMemo(() => {
    if (!allLegs) return null;
    return groupTrips(allLegs.map(resolveLeg), { placeIndex }).map((g) => ({
      key: g.key,
      name: g.name,
      named: isNamedGroupKey(g.key),
      startDate: g.startDate,
      endDate: g.throughDate ?? g.endDate,
      legCount: g.legs.length,
      tripGroupId: g.tripGroupId,
    }));
  }, [allLegs, placeIndex, resolveLeg]);

  // The trips immediately before and after this one, or null. Includes lone
  // unnamed legs: folding a stray leg into the trip beside it is the commonest
  // reason to combine anything.
  const neighbours = useMemo(
    () => (tripRows ? adjacentTrips(tripRows, decodedKey) : { previous: null, next: null }),
    [tripRows, decodedKey]
  );

  // The item's other NAMED trips, for Rename's collision warning. Off the same
  // read: this used to be a second whole-item fetch fired when the dialog
  // opened, which is the read the page was already doing.
  const otherTripNames = useMemo(() => {
    if (!tripRows) return null;
    return tripRows
      // Only genuinely named groups: an unnamed leg's title is a from-to
      // fallback, not a name anything could collide with.
      .filter((row) => row.named && row.key !== decodedKey)
      .map((row) => row.name);
  }, [tripRows, decodedKey]);

  // The identity a new leg needs to carry to stay in this group ({} for an
  // unnamed, single-leg group). Both halves come off the URL key where they can,
  // so Add works before the legs have loaded: the key holds the id outright, and
  // holds a legacy name lowercased -- so the loaded group's spelling wins once
  // we have it. Held in a ref because Add's callback runs after a create
  // round-trip, by which point the render it closed over -- and the group it
  // saw -- may be stale.
  const newLegBinding = useRef({});
  useEffect(() => {
    newLegBinding.current = isNamedGroupKey(decodedKey)
      ? tripIdentityDefaults({
        name: group?.name ?? groupNameFromKey(decodedKey),
        tripGroupId: tripGroupIdFromKey(decodedKey),
      })
      : {};
  }, [group, decodedKey]);

  useEffect(() => {
    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a
    // queue subscription until (or unless) the write finishes.
    const leaving = new AbortController();
    async function load() {
      try {
        // Seed from router state for an instant first paint.
        const stateItem = location.state?.item?.id === itemId ? location.state.item : null;
        const stateGroup = location.state?.group?.key === decodedKey ? location.state.group : null;
        if (stateItem) setItem(stateItem);

        // ...but only when those legs are actually current. The page that
        // handed them over autosaves, so it can navigate here with a write
        // still in flight -- the snapshot it passed was taken before that
        // write, and painting it shows the user their edit "not saved".
        //
        // A settled lane doesn't *prove* the snapshot is fresh: the handing-
        // over page rebuilds its legs in its save's onSaved, so a snapshot
        // captured before that callback ran is stale whatever the lane says.
        // What makes this safe is that we always fetch afterwards -- the lane
        // check only decides whether the first paint is worth showing. (Trip
        // detail can't lean on that; a handover there skips the fetch, which
        // is why it reads the lane before its first await.)
        //
        // A different hazard for legs the previous page WROTE rather than read
        // (the trips list's JSON import, this page's own Rename): no lane to
        // wait on, and the fetch below can miss them outright. See
        // withKnownLegs.
        //
        // A cascade is the same hazard wearing different clothes, and it is the
        // one this page is most exposed to: it is one click up the breadcrumb
        // from the leg whose Days the user just changed, and re-dating the legs
        // AFTER that one is precisely what a cascade does. Those writes bypass
        // the save queue (persistLegDates goes straight to the database), so no
        // lane reports them -- hence the second wait below.
        const justWritten = location.state?.legsJustWritten === true;
        const handedOverLegs = stateGroup?.legs ?? [];
        // Whether the SNAPSHOT is worth painting is a question about its own
        // legs: an unrelated leg saving elsewhere doesn't make these rows old.
        const snapshotStale = handedOverLegs.some((leg) => saveQueue.isPending(legLane(leg.id)))
          || legCascadesPending();
        if (handedOverLegs.length > 0 && !snapshotStale) setLegs(handedOverLegs);

        // Whether READING now is safe is the other question, and it is about
        // every leg write in flight -- including the ones this page was never
        // handed. The breadcrumb is exactly that case and it is how the user
        // gets here from the leg they just edited: no router state, so no leg
        // ids and no lanes; and the save is still in flight, so the cascade
        // that will re-date the siblings hasn't been queued yet and
        // legCascadesPending() is false as well. Both checks above see nothing,
        // the fetch reads the pre-save dates, and the next leg keeps the start
        // date the old `days` gave it with nothing to correct it later.
        const lanes = pendingLegLanes();
        const writesPending = lanes.length > 0 || legCascadesPending();

        // Same reason the fetch has to wait: reading now would serve back the
        // same pre-save row, and nothing re-fetches afterwards to correct it.
        //
        // Say so while we wait. Skipping the instant paint already costs the
        // user the only thing on screen, and `isPending` counts a lane that is
        // sitting in retry backoff -- so a save being retried over a flaky
        // connection can hold this for several seconds. An empty group with no
        // explanation reads as "this trip has no legs".
        if (writesPending) {
          showLoading('Finishing saved changes...');
          await saveQueue.settledAll(lanes, { signal: leaving.signal });
          if (!mounted) return;
          // AFTER the lanes, never before: the leg page queues its cascade from
          // the save's onSaved, which the queue runs before it notifies the
          // subscribers the wait above resolves on. Snapshotting the chain any
          // earlier would miss the very cascade we are here to wait for.
          await settledLegCascades();
          if (!mounted) return;
        }

        // Load the item for breadcrumbs if we didn't get it from state.
        const itemPromise = stateItem ? Promise.resolve(stateItem) : Database.retrieveItem(itemId);
        // Always fetch the full leg set so a partially-loaded group fills in.
        // The ITEM's legs rather than listTripsForGroupKey's, which is the same
        // request -- that helper pages every leg and groups client-side -- with
        // the other trips kept instead of discarded, so this page can name the
        // trips either side of the one it is showing.
        const legsPromise = Database.listTripsForItem(itemId);

        const [loadedItem, loadedAllLegs] = await Promise.all([itemPromise, legsPromise]);
        if (!mounted) return;
        setItem(loadedItem);
        // Two different answers to one GSI lag, and the difference is what each
        // list is narrowed by. `legs` holds only what the fetch grouped under
        // THIS key, so a row still indexed under the old name is simply absent
        // and the fetch keeps winning for everything it does return
        // (withKnownLegs appends what is missing). The item's list has that row,
        // carrying its pre-write identity -- which would draw a phantom trip
        // beside this one, offering to combine with the trip just combined --
        // so there the written row replaces it.
        setAllLegs(justWritten ? withWrittenLegs(loadedAllLegs, handedOverLegs) : loadedAllLegs);
        const loadedLegs = legsOfGroup(loadedAllLegs, decodedKey);
        setLegs(justWritten ? withKnownLegs(loadedLegs, handedOverLegs) : loadedLegs);
        if (pendingShareNotice.current) {
          showText(pendingShareNotice.current);
          pendingShareNotice.current = null;
        } else {
          hideStatus();
        }
      } catch (err) {
        if (mounted) showError(err);
      }
    }
    load();
    return () => { mounted = false; leaving.abort(); };
    // location.state is read once on mount; re-running on itemId/key change is
    // enough (a different group means a different URL).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, decodedKey]);

  // A warning the importing page couldn't show, because it navigated here.
  // Shown once, beside the trip it is about, rather than on the page the user
  // has already left. Only a places-phase failure produces one -- the import
  // itself succeeded, or we wouldn't be here.
  useEffect(() => {
    if (location.state?.notice) showText(location.state.notice);
    // Mount only: re-firing on a later render would resurrect a dismissed
    // notice, and router state survives a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a leg's detail page, handing over item + leg so it can skip a fetch.
  //
  // The handover is always the STORED leg, never the resolved copy this page
  // displays. Every caller reaches this holding a `displayLegs` element -- the
  // table rows as well as TripGroupMap, since the rename overlay landed on the
  // table too. A pre-resolved handover would seed the leg page with a
  // route its own resolve then agrees with, leaving `routeGen` at 0 -- so that
  // one navigation would quietly skip the lazy write-back. Nothing is lost
  // (the display is correct and a later edit or a fresh load still persists
  // it), but "write paths use the unresolved legs" should hold without an
  // asterisk. Normalizing here rather than at the call site covers whoever
  // wires up the next one.
  const onDetails = useCallback((trip) => {
    // `|| trip` is for onAdded's freshly created leg, which isn't in `legs` yet.
    const stored = (legs || []).find((leg) => leg.id === trip.id) || trip;
    navigate(urlForTrip(itemId, stored.id), { state: { item, trip: stored } });
  }, [navigate, itemId, item, legs]);

  // A new leg was created. Defaults (name / date / from) were persisted on
  // the CREATE so the leg stays in this group without a follow-up save -- open
  // its detail page for editing, same as clicking a row.
  function onAdded(newTrip) {
    navigate(urlForTrip(itemId, newTrip.id), { state: { item, trip: newTrip } });
  }

  // Header Add: create a stub bound to this trip and edit it on the detail
  // page. The identity goes in the CREATE so the leg stays here even if the
  // user navigates away without touching the form.
  async function handleAdd() {
    await Database.createTripWithUI(
      itemId, showText, showError, hideStatus, onAdded, { ...newLegBinding.current }
    );
  }

  // Add the next leg of the trip -- see nextLegDefaults for what it inherits.
  // Those defaults go in the CREATE so the leg keeps them, and stays in this
  // group, even if the detail page is left without further edits. The leg page's
  // own Add Next Leg builds the same input from the same helper.
  async function handleAddAfterLast() {
    const lastLeg = sortedLegs[sortedLegs.length - 1];
    const defaults = nextLegDefaults(lastLeg, newLegBinding.current);
    await Database.createTripWithUI(itemId, showText, showError, hideStatus, onAdded, defaults);
  }

  // Write the dates a cascade produced and merge the saved rows back in.
  // Shared by move and delete: both re-date legs and both recover the same way.
  //
  // The rows move only once the writes come back, which is how every other
  // save in this app works. Painting first and writing after looks snappier,
  // but a cascade is SEVERAL writes: leaving the page mid-flight cancels them,
  // and the usual result is some landing and some not, so the legs end up with
  // a schedule that is neither the old one nor the new -- after the table had
  // already shown it succeeding. The controls stay disabled for the duration
  // either way.
  async function applyDateUpdates(updates) {
    // Only `date` is sent, so the reserved `fields` blob (the map route) is
    // left untouched -- same reason the trip detail editor never includes it.
    const saved = await persistLegDates(updates, itemId);
    // Guard on the id: a resolved-but-empty mutation result would otherwise
    // throw a TypeError here, and the caller's catch would report THAT instead
    // of whatever actually went wrong with the write.
    const byId = new Map(saved.filter((row) => row?.id).map((row) => [row.id, row]));
    // Nothing was painted ahead of this, so these stored dates ARE the new
    // order -- sortedLegs re-sorts on them.
    setLegs((current) => (current ?? []).map((t) => byId.get(t.id) ?? t));
    // And into the item's copy, because a reorder moves this trip's dates --
    // which is exactly what decides which trips are next to it.
    setAllLegs((current) => (current === null ? current : current.map((t) => byId.get(t.id) ?? t)));
  }

  // A failure above is usually PARTIAL -- some legs took their new date and
  // some kept the old one -- so what's stored is neither the old schedule nor
  // the one that was asked for. Re-read rather than guess. (The read goes
  // through tripsByItemId, a GSI keyed on the `date` we just rewrote, so it can
  // lag by a moment; it's still the best answer available.) If even that fails,
  // leave state alone: it still holds the pre-edit order, and an explicit
  // rollback here would clobber anything the header's Add appended while these
  // writes were in flight.
  async function rereadLegs() {
    try {
      const reloaded = await Database.listTripsForItem(itemId);
      setAllLegs(reloaded);
      setLegs(legsOfGroup(reloaded, decodedKey));
    } catch {
      /* keep what's on screen -- see above */
    }
  }

  // Reorder a leg by rewriting dates (reorderLegsByDate explains why).
  async function handleMove(index, delta) {
    const { updates } = reorderLegsByDate(sortedLegs, index, delta);
    if (updates.length === 0) return;

    setBusy(true);
    try {
      await applyDateUpdates(updates);
      // No hideStatus() here: a reorder never shows a status (it disables the
      // arrows instead), so the only message it could clear belongs to someone
      // else -- the header's Add stays live throughout a move.
    } catch (err) {
      showError(err);
      await rereadLegs();
    } finally {
      setBusy(false);
    }
  }

  // Export this trip's legs (freshly fetched so a partially-painted page
  // still exports everything), routes included.
  async function handleExport() {
    try {
      setExporting(true);
      showText('Exporting...');
      // The gazetteer alongside the legs, so the file can recreate its places
      // in another environment. loadPlaceIndex never rejects (empty maps on
      // failure just omit the section): an export must not fail because the
      // place list is down.
      const [allLegs, placeIndex] = await Promise.all([
        Database.listTripsForGroupKey(itemId, decodedKey),
        loadPlaceIndex(),
      ]);
      const payload = serializeTrip(allLegs, { placeIndex });
      downloadJson(suggestedTripFileName(payload.trip.name), payload);
      hideStatus();
    } catch (err) {
      showError(err);
    } finally {
      setExporting(false);
    }
  }

  // Open the Combine dialog for one neighbour, and read what the merge would
  // end for good behind it.
  //
  // Read on open rather than on load because most visits never open this, and
  // both reads are whole-item pages of a GSI. A failure is swallowed on purpose
  // -- a warning we couldn't compute is no reason to block a merge, or to put
  // an error on a page whose data loaded fine. That is openRename's old bargain,
  // and it is the same bargain: the repointers themselves are best-effort too.
  function openCombine(direction, group) {
    setCombining({ direction, group });
    setCombineLosses(null);
    Promise.all([
      shareTripAvailable() ? Database.listTripSharesForItem(itemId) : Promise.resolve([]),
      tripWeatherEmailAvailable()
        ? Database.listTripWeatherSubscriptionsForItem(itemId)
        : Promise.resolve([]),
    ])
      .then(([shares, subscriptions]) => {
        const keys = [decodedKey, group.key];
        const losses = [];
        // The SAME keeper rule the repoint applies, asked of the same rows --
        // a second copy of it here would be free to disagree with the one that
        // does the deleting.
        const extraShares = tripShareRowsForMerge(shares, keys, decodedKey).slice(1);
        if (extraShares.length > 0) {
          losses.push(
            'Both trips have a share link. The other trip\'s link will stop working.'
          );
        }
        const extraSubs = tripWeatherRowsForMerge(subscriptions, keys, decodedKey).slice(1);
        for (const row of extraSubs) {
          losses.push(
            row.email
              ? `The weather email to ${row.email} for the other trip will be cancelled.`
              : 'The other trip\'s weather email will be cancelled.'
          );
        }
        setCombineLosses(losses);
      })
      .catch(() => { /* no warning, see above */ });
  }

  // Rename the trip: write the new Trip Name to every leg.
  //
  // A trip is still not a row -- the name lives on each leg, so a list or an
  // export needs no join to show it -- which is why this is the one edit on this
  // page that touches all of them at once. Throws on failure; the dialog reports
  // it inline.
  //
  // What a trip IS addressed by is its id, and an id a rename cannot move: for a
  // 'g:' group everything below the leg writes is dead code. That is the whole
  // tail of this function -- the re-address, the GSI-lag handover, and the
  // best-effort share repoint whose failure the page had to apologise for. It
  // stays for a legacy 'n:' group, which is keyed by the very thing being
  // changed, until the backfill has bound one.
  async function handleRename(name) {
    // Read the legs fresh, and wait out any leg whose own edit is still in
    // flight: a queued name from that leg's detail page landing after this
    // would drop the leg back into the old trip. loadGroupLegs does both.
    const current = await loadGroupLegs(itemId, decodedKey);
    // Legs already spelling the name exactly are left alone -- a rename that
    // only changes capitalization still has to write the rest.
    const targets = current.filter((leg) => String(leg.name ?? '') !== name);

    // allSettled, for the same reason persistLegDates uses it: these are
    // independent writes, and reporting the first rejection while the others
    // are still in flight would leave the caller repairing a moving database.
    const results = await Promise.allSettled(
      targets.map((leg) => Database.updateTrip({ id: leg.id, itemId, name }))
    );
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) {
      // A partial failure SPLITS the trip: some legs carry the new name and
      // some the old, which is two groups. Re-read under the key we're still
      // on so the page shows what is actually stored, and let the dialog say
      // what went wrong.
      await rereadLegs();
      throw failed.reason;
    }

    const saved = results.map((r) => r.value);
    const byId = new Map(saved.filter((row) => row?.id).map((row) => [row.id, row]));
    const renamed = current.map((leg) => byId.get(leg.id) ?? leg);

    setShowRename(false);
    setLegs(renamed);
    // The item's copy carries these legs too, and the collision warning and the
    // Combine buttons are both derived from it -- so it has to take the new
    // name here or it would still describe the trip as it was.
    setAllLegs((currentAll) =>
      (currentAll === null ? currentAll : withWrittenLegs(currentAll, renamed))
    );

    // Only a rename that changes the KEY moves the page. For a trip with an id
    // that is never -- which is why the key is derived from a WRITTEN leg here
    // rather than from `{ name }` alone: the latter has no id to read and would
    // navigate every bound trip off a URL that still addresses it perfectly.
    // (For a legacy group it is the old rule exactly: the key is lowercased, so
    // fixing capitalization doesn't move it.)
    const nextKey = tripGroupKey({ ...renamed[0], name });
    if (nextKey === decodedKey) return;

    // The public share link (if any) addresses the group by this key, so a
    // rename strands it -- repoint the share row at the new key. Best-effort:
    // the rename itself succeeded, and failing it over its share would leave
    // the user re-renaming a trip that is already renamed. The warning waits
    // in pendingShareNotice for the post-navigate reload (see its comment).
    try {
      const share = await Database.getTripShareForGroup(itemId, decodedKey);
      if (share) await Database.updateTripShareGroupKey(share.id, nextKey);
    } catch {
      pendingShareNotice.current =
        'The trip was renamed, but its share link could not be updated and may stop working. Open Share to check it.';
    }

    // Hand the written rows over and say so: the fetch the new page runs goes
    // through tripsByItemId, a GSI that can still be grouping these legs under
    // the OLD name -- in which case it finds nothing under the new key and the
    // trip would read as empty. See withKnownLegs. `replace` because the URL we
    // are leaving no longer addresses anything.
    const nextGroup = groupTrips(renamed).find((g) => g.key === nextKey);
    navigate(urlForTripGroup(itemId, nextKey), {
      replace: true,
      state: { item, group: nextGroup, legsJustWritten: true },
    });
  }

  // Combine this trip with the one before or after it: write one name and one
  // tripGroupId to every leg of both.
  //
  // handleRename's shape, because it is the same kind of edit -- a trip is not
  // a row, so a trip-level change is a write to each of its legs -- with two
  // differences. It reads the WHOLE item rather than this group (the other
  // trip's legs are the point), and it writes `tripGroupId` as well as `name`,
  // which is what makes this a re-filing rather than a rename.
  //
  // Throws on failure; the dialog reports it inline.
  async function handleCombine(targetKey, name) {
    // Fresh, and settled: loadAllTripsFresh waits out any leg whose own edit is
    // still in flight and then the leg-date cascades, then re-reads. No signal
    // is passed -- this is the read half of a write that has to happen, so an
    // unmount must not abandon it half way (loadGroupLegs is deliberately
    // un-abortable for the same reason).
    const fresh = await loadAllTripsFresh(itemId);
    const groups = groupTrips(fresh);
    const source = findTripGroup(groups, decodedKey);
    const target = findTripGroup(groups, targetKey);
    if (!source || !target || source.key === target.key) {
      throw new Error('These trips have changed since the page loaded. Reload and try again.');
    }

    // The SOURCE's id wins, so a bound trip keeps its URL, its share link and
    // its weather schedule -- see mergedTripIdentity.
    const identity = mergedTripIdentity({
      sourceGroupId: source.tripGroupId,
      targetGroupId: target.tripGroupId,
      name,
    });

    const merged = [...source.legs, ...target.legs];
    const targets = recordsToRefile(merged, identity);

    // allSettled, as handleRename does and for the same reason: these are
    // independent writes, and reporting the first rejection while the others
    // are in flight would leave the caller repairing a moving database.
    const results = await Promise.allSettled(
      targets.map((leg) => Database.updateTrip({ id: leg.id, itemId, ...identity }))
    );
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) {
      // A partial failure leaves some legs refiled and some not, which is still
      // two trips. Re-read so the page shows what is actually stored.
      await rereadLegs();
      throw failed.reason;
    }

    const saved = results.map((r) => r.value).filter((row) => row?.id);
    const byId = new Map(saved.map((row) => [row.id, row]));
    const combinedLegs = merged.map((leg) => byId.get(leg.id) ?? leg);

    // The seam. Two trips a month apart become one trip whose boundary leg
    // still says it is a one-day stay, and the next cascade anywhere at or
    // before it would drag the absorbed trip a month earlier. Written after the
    // refile so it is measured on the merged order, and only `days` goes out.
    const sourceIds = new Set(source.legs.map((leg) => leg.id));
    const ordered = orderedMergedLegs(combinedLegs);
    const dayUpdates = seamDaysUpdates(ordered, (leg) => sourceIds.has(leg.id));
    // Kept local rather than parked in pendingShareNotice, which is a single
    // slot: a share or weather notice raised below would overwrite this one,
    // and the reader would never learn the seam wasn't saved. Everything this
    // merge has to say ends up in `notices` and goes out as one sentence.
    let seamNotice = null;
    if (dayUpdates.length > 0) {
      const dayResults = await Promise.allSettled(
        dayUpdates.map((u) => Database.updateTrip({ id: u.id, itemId, days: u.days }))
      );
      for (const result of dayResults) {
        if (result.status === 'fulfilled' && result.value?.id) {
          byId.set(result.value.id, result.value);
        }
      }
      // A failed seam write is not worth undoing a landed merge over: the trip
      // is combined, and the only cost is that a later cascade would re-date
      // from a stale `days`. Say so rather than throw.
      if (dayResults.some((r) => r.status === 'rejected')) {
        seamNotice =
          'The gap between them could not be saved -- check the Days column on the leg where they join.';
      }
    }

    const written = merged.map((leg) => byId.get(leg.id) ?? leg);

    // Derived from a WRITTEN leg, never rebuilt from `{ name }` -- handleRename's
    // rule. For a bound source this is the key we are already on.
    const mergedKey = tripGroupKey(written[0]);

    // Whatever pointed at the trip that has just stopped existing. Best-effort
    // and non-throwing: the legs are already refiled by the time these run.
    const oldKeys = [decodedKey, target.key];
    const shareResult = await repointTripShares(itemId, oldKeys, mergedKey);
    const weatherResult = await repointTripWeatherSubscriptions(
      itemId, oldKeys, mergedKey, identity.name
    );
    // Everything the merge has to report, in one sentence. The seam leads: it
    // is about the trip's own data, where the other two are about what pointed
    // at it.
    const notices = [];
    if (seamNotice) {
      notices.push(seamNotice);
    }
    if (shareResult.revoked > 0) {
      notices.push('One of the two share links was revoked; the other opens the combined trip.');
    }
    if (weatherResult.cancelled > 0) {
      notices.push(
        weatherResult.cancelledEmails.length > 0
          ? `The weather email to ${weatherResult.cancelledEmails.join(', ')} was cancelled.`
          : 'One of the two weather email schedules was cancelled.'
      );
    }
    if (weatherResult.repointed > 0) {
      notices.push('The weather email now follows the combined trip -- open Weather Email to check its send window.');
    }
    const notice = notices.length > 0 ? `The trips were combined. ${notices.join(' ')}` : null;

    setCombining(null);
    setCombineLosses(null);

    if (mergedKey === decodedKey) {
      // No navigation, so nothing reloads -- refresh both copies from what was
      // written, or the Combine buttons would still offer the trip that has
      // just been absorbed.
      setLegs(written);
      setAllLegs((currentAll) =>
        (currentAll === null ? currentAll : withWrittenLegs(currentAll, written))
      );
      if (notice) showText(notice);
      return;
    }

    // Two legacy trips minted a fresh id, so the URL we are on no longer
    // addresses anything. Hand the written rows over and say so: the fetch the
    // new page runs goes through tripsByItemId, a GSI that can still be
    // grouping these legs under their old keys.
    if (notice) {
      pendingShareNotice.current = notice;
    }
    const nextGroup = groupTrips(written).find((g) => g.key === mergedKey);
    navigate(urlForTripGroup(itemId, mergedKey), {
      replace: true,
      state: { item, group: nextGroup, legsJustWritten: true },
    });
  }

  // Delete the whole trip: every leg, then the rows that pointed at it.
  //
  // A trip is still not a row of its own -- it is the shared identity of its
  // legs -- so this is one delete per leg, and the trip stops existing when the
  // last of them goes. That is why it sits up here beside Rename rather than in
  // the table: both are edits to the TRIP, expressed as a write to each leg.
  // Deleting a single leg is still the leg's own page's job (see the note above
  // LegRow).
  //
  // The legs are re-read first, for the same reason handleRename re-reads them:
  // this page can be showing a partially-loaded group (a paginated list, or the
  // item card's bounded window, handed it over), and a leg left behind would
  // rebuild the trip under the same key the moment anything reloaded. Progress
  // is reported through the dialog's own status line -- the deletes run one at
  // a time, and the page's status message is behind the modal.
  async function confirmDelete(status) {
    try {
      status.showLoading('Deleting trip...');

      // Cancel the lanes of the legs we already know about BEFORE anything
      // else -- the rule every delete flow in this app follows, and here it is
      // also what keeps the read below quick. The user has asked for these rows
      // to go, so an edit sitting in the autosave debounce is moot; leaving it
      // armed would make loadGroupLegs wait the debounce out and then write to
      // a row we are about to delete. (Trip.jsx's leg delete cancels first for
      // the same reason.)
      (legs ?? []).forEach((leg) => saveQueue.cancel(legLane(leg.id)));

      // Fresh, and settled: loadGroupLegs waits out any leg whose own edit is
      // still in flight, so a leg an autosave is about to move INTO this trip
      // is either in this list or genuinely somewhere else.
      const targets = await loadGroupLegs(itemId, decodedKey);

      // And again for whatever that read turned up that we hadn't seen: a lane
      // re-armed while the read was in flight, or one parked in error (which
      // isPending excludes, so the wait above skipped it).
      targets.forEach((leg) => saveQueue.cancel(legLane(leg.id)));

      for (let i = 0; i < targets.length; i++) {
        await Database.deleteTrip(targets[i].id);
        status.showLoading(
          `Deleted ${i + 1} of ${targets.length} ${targets.length === 1 ? 'leg' : 'legs'}...`
        );
      }

      // The trip is gone, so what pointed at it has to stop. Both helpers are
      // best-effort and neither throws (they swallow and log): the legs are
      // already deleted by the time these run, and turning a completed delete
      // into an error would invite the user to retry a trip that no longer
      // exists. The authoritative cleanup for the weather email is
      // TaskPogoTripWeatherMail's, which deletes any subscription whose key no
      // longer resolves to a trip.
      await removeTripWeatherSubscriptionsForGroup(itemId, decodedKey);
      await removeTripShareForGroup(itemId, decodedKey);

      status.hideStatus();
      setShowDelete(false);
      // The trips list, not the group page we are on -- it addresses nothing
      // now. `replace` so Back doesn't return to it. Bare navigate: the list
      // fetches its own data and reads no router state we could hand it. (Same
      // destination Trip.jsx falls back to when its delete takes the last leg.)
      navigate(urlForTrips(itemId), { replace: true });
    } catch (err) {
      // A failure part way through leaves some legs deleted and some not, which
      // is a shorter trip rather than a broken one. Close the dialog, report it
      // beside the trip, and re-read so the table shows what actually survived.
      setShowDelete(false);
      showError(err);
      await rereadLegs();
    }
  }

  const groupName = group?.name ?? 'Trip';

  // This trip as CombineTripsModal describes a trip. `named` is asked of the
  // KEY, not of the name: an unnamed lone leg's name is the app's own from-to
  // title, which must never be offered back as a trip name to save.
  const thisTripRow = useMemo(() => ({
    key: decodedKey,
    name: groupName,
    named: isNamedGroupKey(decodedKey),
    dateRange: group ? tripGroupDateRange(group) : '',
    countLabel: legCountLabel(legs?.length ?? 0),
  }), [decodedKey, groupName, group, legs]);

  const combineTarget = combining?.group ?? null;
  const combineTargetRow = combineTarget && {
    ...combineTarget,
    dateRange: tripGroupDateRange({
      startDate: combineTarget.startDate,
      throughDate: combineTarget.endDate,
    }),
    countLabel: legCountLabel(combineTarget.legCount),
  };

  return (
    <div className='pb-2'>
      <ItemBreadcrumbs
        item={item}
        extras={[
          { name: 'Trips', url: urlForTrips(itemId) },
          { name: groupName, url: urlForTripGroup(itemId, decodedKey) },
        ]}
      />

      <StatusMessage />

      {legs && legs.length === 0 ? (
        <p className="text-center text-body-secondary">
          This trip has no legs.
        </p>
      ) : null}

      <DataCard
        header={
          <CardHeader
            text={groupName}
            // No Add here: the foot of the itinerary already has one, and it
            // continues the trip from its last leg rather than starting a leg
            // adrift of it. Rename takes that slot -- it is the only action on
            // this page that is about the TRIP rather than about a leg.
            extraActions={
              <>
                <Button variant="outline-secondary" onClick={handleExport} disabled={exporting}>
                  Export JSON
                </Button>
                {/* Share needs a group to point at, and stays hidden until the
                    share-trip backend is deployed (weatherAvailable pattern:
                    the Function URL is absent from amplify_outputs.json until
                    then). */}
                {shareTripAvailable() && legs && legs.length > 0 && (
                  <Button variant="outline-secondary" onClick={() => setShowShare(true)}>
                    Share
                  </Button>
                )}
                {/* Gated like Share, but on a flag amplify/backend.ts
                    publishes rather than a Function URL: this feature is
                    reached entirely through AppSync, so nothing appears in
                    amplify_outputs.json on its own when the backend lands.
                    See src/utilities/tripWeatherEmail.mjs.

                    DISABLED rather than hidden when nothing is on the map: a
                    trip with no placed waypoints has no bounds and its email
                    would be empty, and the reader should learn why rather than
                    wonder where the button went. */}
                {tripWeatherEmailAvailable() && legs && legs.length > 0 && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowWeatherEmail(true)}
                    disabled={!anyLegOnMap}
                    title={
                      anyLegOnMap
                        ? undefined
                        : 'Put this trip on the map to get a weather email for it.'
                    }
                  >
                    Weather Email
                  </Button>
                )}
                {/* Nothing to rename until we know what the trip's legs are --
                    the name is written to each of them. */}
                {legs && legs.length > 0 && (
                  <Button variant="primary" onClick={() => setShowRename(true)}>Rename</Button>
                )}
                {/* Gated on the legs for the same reason, and more strictly:
                    a trip with no legs has already stopped existing, and the
                    page says so above. Disabled during a reorder -- that is a
                    cascade of date writes to the very rows this would delete.
                    Rendered here rather than through CardHeader's own
                    deleteTapped slot, which offers no way to disable it; the
                    variant matches so the two are the same button. */}
                {legs && legs.length > 0 && (
                  <Button
                    variant="outline-danger"
                    onClick={() => setShowDelete(true)}
                    disabled={busy}
                  >
                    Delete
                  </Button>
                )}
              </>
            }
          />
        }
        body={legs ? (
          <>
            {group && (
              <div className="text-center text-body-secondary mb-2">
                <div>{tripGroupDateRange(group)}</div>
                {tripGroupSummary(group, { placeIndex }) && (
                  <div>{tripGroupSummary(group, { placeIndex })}</div>
                )}
              </div>
            )}

            {/* Combining is about the TRIP, like Rename, but it belongs here
                rather than up in the header: what "before" and "after" mean is
                decided by the dates directly above, and the buttons have to
                carry the other trip's NAME to be worth clicking, which is more
                than an action row can hold. Nothing renders when this trip has
                no neighbour, which is most trips.

                Disabled during a reorder: that is a cascade of date writes to
                the very rows this would refile, and it moves the dates that
                decide which trip is next. */}
            {legs && legs.length > 0 && (neighbours.previous || neighbours.next) && (
              <div className="d-flex justify-content-center flex-wrap gap-2 mb-3 trip-combine">
                {neighbours.previous && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => openCombine('previous', neighbours.previous)}
                  >
                    <i className="fa-solid fa-chevron-left me-1" aria-hidden="true"></i>
                    Combine with "{neighbours.previous.name}"
                  </Button>
                )}
                {neighbours.next && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => openCombine('next', neighbours.next)}
                  >
                    Combine with "{neighbours.next.name}"
                    <i className="fa-solid fa-chevron-right ms-1" aria-hidden="true"></i>
                  </Button>
                )}
              </div>
            )}

            <Table responsive hover className="align-middle mb-0 trip-legs-table">
              <thead className="bg-body-tertiary">
                <tr>
                  {columns.map((field) => (
                    <th key={field.key} scope="col">{field.name}</th>
                  ))}
                  {/* Both of these columns hold nothing but icon buttons, and
                      below md their HEADINGS were what set their width --
                      measured at 375px, 50px of "Order" over two 34px chevrons
                      and 38px of "Details" over one. visually-hidden rather
                      than dropped: a screen reader gets the same column names
                      at either width, and up/down chevrons and a right chevron
                      caption themselves to a reader who can see them. */}
                  {showOrder && (
                    <th scope="col" className="text-center">
                      {isMdUp ? 'Order' : <span className="visually-hidden">Order</span>}
                    </th>
                  )}
                  <th scope="col" className="text-center">
                    {isMdUp ? 'Log' : <span className="visually-hidden">Log</span>}
                  </th>
                  <th scope="col" className="text-end">
                    {isMdUp ? 'Details' : <span className="visually-hidden">Details</span>}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Display legs, not `sortedLegs`: same rows in the same order,
                    with the gazetteer's current names in From/To. Every handler
                    below is id- or index-based (onDetails re-looks-up the
                    stored leg, handleMove indexes `sortedLegs`), so no write
                    path reads these copies. */}
                {(displayLegs ?? []).map((leg, index) => (
                  <LegRow
                    key={leg.id ?? index}
                    trip={leg}
                    position={index + 1}
                    placeIndex={placeIndex}
                    onDetails={onDetails}
                    onCopyToLog={copyLegToLogbook}
                    // A reorder rewrites the dates this would copy, and a copy
                    // in flight must not be started twice (the hook refuses
                    // either way -- this is what makes the refusal visible).
                    copyDisabled={busy || copying}
                    onMove={(delta) => handleMove(index, delta)}
                    canMoveUp={index > 0}
                    canMoveDown={index < sortedLegs.length - 1}
                    disabled={busy}
                    columns={columns}
                    showOrder={showOrder}
                    columnCount={columnCount}
                    isMdUp={isMdUp}
                  />
                ))}

                {/* Continue the trip from where its last leg ended -- or, for
                    a group with no legs at all, start one (there is nothing to
                    continue from, so it gets the plain named stub the header's
                    Add used to make). Only for a NAMED group: legs group by
                    Trip Name, so a leg added to an unnamed group has nothing to
                    share and would stand alone as its own trip the moment the
                    page reloaded. */}
                {canAddLeg && sortedLegs && (
                  <tr>
                    <td colSpan={columnCount} className="text-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Add leg"
                        disabled={busy}
                        onClick={sortedLegs.length > 0 ? handleAddAfterLast : handleAdd}
                      >
                        <i className="fa-solid fa-plus me-1" aria-hidden="true"></i>
                        Add leg
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

          </>
        ) : null}
      />

      {anyLegOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap legs={displayLegs} onSelectLeg={onDetails} />
        </Suspense>
      )}

      {/* Seeded blank for an UNNAMED group: `group.name` there is the leg's
          from-to title, not a name anyone typed, and offering it back as the
          current name would invite the user to save the app's own wording. */}
      <RenameTripModal
        show={showRename}
        currentName={isNamedGroupKey(decodedKey) ? (group?.name ?? '') : ''}
        otherTripNames={otherTripNames}
        // A trip with an id does not merge into a name it happens to share.
        merges={!isTripGroupIdKey(decodedKey)}
        onHide={() => setShowRename(false)}
        onRename={handleRename}
      />

      <CombineTripsModal
        show={combining !== null}
        direction={combining?.direction}
        thisTrip={thisTripRow}
        otherTrip={combineTargetRow}
        losses={combineLosses}
        onHide={() => { setCombining(null); setCombineLosses(null); }}
        onCombine={(name) => handleCombine(combining.group.key, name)}
      />

      {/* The leg count is in the body rather than implied: this button is one
          click from a table of legs, and "and all 6 of its legs" is the only
          thing on screen that says a partially-loaded group is about to lose
          more rows than the reader can currently see. */}
      <Confirm
        show={showDelete}
        onConfirm={confirmDelete}
        onHide={() => setShowDelete(false)}
        title={`Delete ${groupName}`}
        body={
          legs && legs.length === 1
            ? 'Delete this trip and its one leg? This cannot be undone.'
            : `Delete this trip and all ${legs?.length ?? 0} of its legs? This cannot be undone.`
        }
        confirmText="Confirm Delete"
      />

      <ShareTripModal
        show={showShare}
        onHide={() => setShowShare(false)}
        itemId={itemId}
        groupKey={decodedKey}
        groupName={groupName}
      />

      <WeatherEmailModal
        show={showWeatherEmail}
        onHide={() => setShowWeatherEmail(false)}
        itemId={itemId}
        groupKey={decodedKey}
        group={group}
        defaultEmail={user?.email}
      />

      {copyToLogModals}
    </div>
  );
};

export default TripGroup;
